import React, { useEffect, useState, useMemo, forwardRef,
  useImperativeHandle, useRef } from "react";
import { useTranslation } from "../../../../../i18n/useTranslation";
import {
  View,
  Text,
  TouchableOpacity,
  FlatList,
  StyleSheet,
  Keyboard,
  Pressable,
} from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import ShieldIcon from "../../../../../../../../assets/images/Shield.svg";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent, {
  personNameFloatingKeyboardProps,
} from "../../../common/FloatingTextInputComponent";
import {
  isPersonNameSegmentValid,
  sanitizeCityInput,
  sanitizeUsStateInput,
} from "../../../../../helper/personNameValidation";
import {
  ADDRESS_LINE_MAX_LENGTH,
  CITY_FIELD_MAX_LENGTH,
  hasRepeatingAddressSpecialCharacters,
  isAddressOrPostalContentValid,
  POSTAL_CODE_MAX_LENGTH,
  sanitizeAddressLineInput,
  sanitizePostalCodeInput,
  US_STATE_FIELD_MAX_LENGTH,
} from "../../../../../helper/addressFieldValidation";
import countryList from "../../../../../../../../apps/quickstage-bff-web/src/services/countryList.json";
import { searchLocationSuggestions, getPlaceDetails } from "../../../../../services/location.service";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

export interface ReceiverAddressStepHandle {
  submit: () => Promise<boolean>;
}

const ReceiverAddressStep = forwardRef<ReceiverAddressStepHandle, Props>(({ onValidationChange }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
      receiverAddress: "",
      receiverCity: "",
      receiverZipCode: "",
      receiverState: "",
    });
  const [addressQuery, setAddressQuery] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  // display the field label from the gffpFieldsByCategory (from gffp response)
  const gffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).gffpFieldsByCategory);
  const receiverInfoFields: any[] = (gffpFieldsByCategory?.["Receiver Information"]) || [];
  const getFieldLabel = (tag: string): string | undefined => {
    const field = receiverInfoFields.find((f: any) => (f.XmlTag ?? f.xmlTag) === tag);
    return field?.FieldLabel ?? field?.fieldLabel;
  };
  const addressPlaceholder = getFieldLabel("senderAddress") || t("ADDRESS");
  const cityPlaceholder = getFieldLabel("senderCity") || t("SENDER_ADDRESS_CITY_PLACEHOLDER");
  const postalPlaceholder = getFieldLabel("senderZipCode") || t("SENDER_ADDRESS_POSTAL_PLACEHOLDER");
  const statePlaceholder = getFieldLabel("senderState") || t("SENDER_ADDRESS_STATE_PLACEHOLDER");
  const receiverAddress = useSessionStore((s: SessionState) => (s as any).receiverAddress) || "";
  const receiverCity = useSessionStore((s: SessionState) => (s as any).receiverCity) || "";
  const receiverZipCode = useSessionStore((s: SessionState) => (s as any).receiverZipCode) || "";
  const receiverState = useSessionStore((s: SessionState) => (s as any).receiverState) || "";
  const setReceiverAddress = useSessionStore((s: SessionState) => (s as any).setReceiverAddress);
  const setReceiverCity = useSessionStore((s: SessionState) => (s as any).setReceiverCity);
  const setReceiverZipCode = useSessionStore((s: SessionState) => (s as any).setReceiverZipCode);
  const setReceiverState = useSessionStore((s: SessionState) => (s as any).setReceiverState);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const requestIdRef = useRef(0);
  const addressSuggestionsDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelDismissAddressSuggestions = () => {
    if (addressSuggestionsDismissTimerRef.current) {
      clearTimeout(addressSuggestionsDismissTimerRef.current);
      addressSuggestionsDismissTimerRef.current = null;
    }
  };

  const scheduleDismissAddressSuggestions = () => {
    cancelDismissAddressSuggestions();
    addressSuggestionsDismissTimerRef.current = setTimeout(() => {
      setShowSuggestions(false);
      addressSuggestionsDismissTimerRef.current = null;
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      cancelDismissAddressSuggestions();
    };
  }, []);

  // USA states sourced from countryList.json
  const usaStates = useMemo(() => {
    try {
      const usa = (countryList as any[]).find((c: any) => c?.CountryCode === "USA");
      return (usa?.States ?? []) as Array<{ stateid: number; stateAbbrivation: string; stateName: string }>;
    } catch {
      return [] as Array<{ stateid: number; stateAbbrivation: string; stateName: string }>;
    }
  }, []);

  const stateQuery = (receiverState || "").trim().toUpperCase();
  const isExactAbbreviation = useMemo(() => {
    return usaStates.some((s) => (s.stateAbbrivation || "").toUpperCase() === stateQuery && stateQuery.length > 0);
  }, [usaStates, stateQuery]);

  const filteredStates = useMemo(() => {
    if (!stateQuery) return usaStates;
    return usaStates.filter((s) =>
      (s.stateName || "").toUpperCase().includes(stateQuery) || (s.stateAbbrivation || "").toUpperCase().includes(stateQuery)
    );
  }, [usaStates, stateQuery]);


  const isValid =
    receiverAddress.trim().length > 0 &&
    isAddressOrPostalContentValid(receiverAddress, ADDRESS_LINE_MAX_LENGTH) &&
    isPersonNameSegmentValid(receiverCity, true, CITY_FIELD_MAX_LENGTH) &&
    receiverZipCode.trim().length > 0 &&
    isAddressOrPostalContentValid(receiverZipCode, POSTAL_CODE_MAX_LENGTH) &&
    receiverState.trim().length === US_STATE_FIELD_MAX_LENGTH &&
    /^[A-Z]{2}$/.test(receiverState.trim());

  // Clear all field errors once the form becomes valid (e.g. user filled all fields)
  useEffect(() => {
    if (isValid) {
      setErrors({
        receiverAddress: "",
        receiverCity: "",
        receiverZipCode: "",
        receiverState: "",
      });
    }
  }, [isValid]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // expose validate function
    useImperativeHandle(ref, () => ({
      submit: async () => {
        const newErrors = {
          receiverAddress: !receiverAddress.trim()
            ? "Address is required"
            : !isAddressOrPostalContentValid(receiverAddress, ADDRESS_LINE_MAX_LENGTH)
              ? receiverAddress.length > ADDRESS_LINE_MAX_LENGTH
                ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: ADDRESS_LINE_MAX_LENGTH })
                : hasRepeatingAddressSpecialCharacters(receiverAddress)
                  ? t("ADDRESS_NO_REPEATING_SPECIAL_CHARS")
                  : t("ADDRESS_POSTAL_INVALID_CHARS")
              : "",
          receiverCity: !receiverCity.trim()
            ? "City is required"
            : !isPersonNameSegmentValid(receiverCity, true, CITY_FIELD_MAX_LENGTH)
              ? receiverCity.length > CITY_FIELD_MAX_LENGTH
                ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: CITY_FIELD_MAX_LENGTH })
                : t("PERSON_NAME_ERROR_LETTERS_ONLY")
              : "",
          receiverZipCode: !receiverZipCode.trim()
            ? "Postal code is required"
            : !isAddressOrPostalContentValid(receiverZipCode, POSTAL_CODE_MAX_LENGTH)
              ? receiverZipCode.length > POSTAL_CODE_MAX_LENGTH
                ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: POSTAL_CODE_MAX_LENGTH })
                : hasRepeatingAddressSpecialCharacters(receiverZipCode)
                  ? t("ADDRESS_NO_REPEATING_SPECIAL_CHARS")
                  : t("ADDRESS_POSTAL_INVALID_CHARS")
              : "",
          receiverState:
            receiverState.trim().length === US_STATE_FIELD_MAX_LENGTH &&
            /^[A-Z]{2}$/.test(receiverState.trim())
              ? ""
              : "State must be 2 letters (e.g., MN)",
        };
        setErrors(newErrors);
  
        // if any error string is non-empty
        const isValid = !Object.values(newErrors).some(Boolean);
        return isValid;
      },
    }));
  
  function getUSStateCode(region?: string, countryCode?: string): string {
    if (!region || countryCode !== "USA") return "";

    const usa = countryList.find((c) => c.CountryCode === "USA");
    if (!usa || !usa.States) return "";

    const state = usa.States.find(
      (s) => s.stateName.toLowerCase() === region.trim().toLowerCase()
    );

    return state?.stateAbbrivation ?? "";
  }
  
  const handleAddressChange = (text: string) => {
      const s = sanitizeAddressLineInput(text);
      setAddressQuery(s);
      setReceiverAddress?.(s);
      if (errors.receiverAddress) {
        setErrors((prev) => ({ ...prev, receiverAddress: "" }));
      }

      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (s.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        setLoading(false);
        return;
      }
    
      debounceTimerRef.current = setTimeout(async () => {
        const currentRequestId = ++requestIdRef.current;
  
        try {
          setLoading(true);
          const results = await searchLocationSuggestions(s);
          if (currentRequestId !== requestIdRef.current) return;
  
          setSuggestions(results || []);
          setShowSuggestions(!!results?.length);
        } catch (e) {
          if (currentRequestId !== requestIdRef.current) return;
            console.error("Location suggestion error", e);
            setSuggestions([]);
            setShowSuggestions(false);
          } finally {
            if (currentRequestId === requestIdRef.current) {
              setLoading(false);
            }
          }
      }, 300);
    };
  
    //Select suggestions
    const handleSelectSuggestion = async (item: any) => {
      cancelDismissAddressSuggestions();
      try {
        const place = await getPlaceDetails(item?.PlaceId);
        const rawAddr =
          `${place?.AddressNumber ?? ""} ${place?.Street ?? ""}`.trim() ||
          place?.Label?.slice(0, 30) ||
          "";
        setReceiverAddress?.(sanitizeAddressLineInput(rawAddr));
        const rawCity =
          place?.Municipality?.slice(0, 20) || place?.Neighborhood?.slice(0, 20) || "";
        setReceiverCity?.(sanitizeCityInput(rawCity, CITY_FIELD_MAX_LENGTH));
        setReceiverZipCode?.(sanitizePostalCodeInput(place?.PostalCode || ""));
        const rawState =
          getUSStateCode(place?.Region, place?.Country) ||
          (place?.Region || "").slice(0, 2).toUpperCase();
        setReceiverState?.(
          sanitizeUsStateInput(rawState).slice(0, US_STATE_FIELD_MAX_LENGTH)
        );
      } catch (e) {
        console.error("GetPlace error", e);
      }
  
      setShowSuggestions(false);
      Keyboard.dismiss();
    };

  return (
    <Pressable
      style={styles.container}
      onPress={() => {
        if (showSuggestions) {
          cancelDismissAddressSuggestions();
          setShowSuggestions(false);
          Keyboard.dismiss();
        }
      }}
    >
      <Text style={styles.title}>{t("SENDER_ADDRESS_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("SENDER_ADDRESS_SUBTITLE")}</Text>
        <View style={styles.form}>
          <FloatingTextInputComponent
            testID="receiverAddress"
            label={addressPlaceholder}
            value={receiverAddress}
            isAutoFocus={false}
            maxLength={ADDRESS_LINE_MAX_LENGTH}
            onChangeText={handleAddressChange}
            errorText={errors.receiverAddress}
            onClearError={() => setErrors((prev) => ({ ...prev, receiverAddress: "" }))}
            autoComplete="street-address"
            onFocus={cancelDismissAddressSuggestions}
            onBlur={scheduleDismissAddressSuggestions}
          />
          {showSuggestions && (
            <View style={styles.dropdownContainer}>
              <FlatList
                keyboardShouldPersistTaps="handled"
                data={suggestions}
                keyExtractor={(item) => item?.PlaceId}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={styles.suggestionItem}
                    onPressIn={cancelDismissAddressSuggestions}
                    onPress={() => handleSelectSuggestion(item)}
                  >
                    <Text style={styles.suggestionText}>{item.Text}</Text>
                  </TouchableOpacity>
                )}
              />
            </View>
          )}
          <FloatingTextInputComponent
            {...personNameFloatingKeyboardProps}
            testID="receiverCity"
            label={cityPlaceholder}
            value={receiverCity}
            maxLength={CITY_FIELD_MAX_LENGTH}
            onChangeText={(val) =>
              setReceiverCity?.(sanitizeCityInput(val, CITY_FIELD_MAX_LENGTH))
            }
            errorText={errors.receiverCity}
            onClearError={() => setErrors((prev) => ({ ...prev, receiverCity: "" }))}
            autoComplete="postal-address-locality"
          />
          <FloatingTextInputComponent
            testID="receiverZipCode"
            label={postalPlaceholder}
            value={receiverZipCode}
            maxLength={POSTAL_CODE_MAX_LENGTH}
            onChangeText={(val) => setReceiverZipCode?.(sanitizePostalCodeInput(val))}
            errorText={errors.receiverZipCode}
            onClearError={() => setErrors((prev) => ({ ...prev, receiverZipCode: "" }))}
            autoComplete="postal-code"
          />
          <FloatingTextInputComponent
            {...personNameFloatingKeyboardProps}
            testID="receiverState"
            label={statePlaceholder}
            value={receiverState}
            maxLength={US_STATE_FIELD_MAX_LENGTH}
            onChangeText={(val) =>
              setReceiverState?.(
                sanitizeUsStateInput(val).slice(0, US_STATE_FIELD_MAX_LENGTH)
              )
            }
            errorText={errors.receiverState}
            onClearError={() => setErrors((prev) => ({ ...prev, receiverState: "" }))}
            autoComplete="postal-address-region"
          />
          {/* USA States dropdown with vertical scroll and search-as-you-type */}
          {!isExactAbbreviation && stateQuery.length > 0 && filteredStates.length > 0 && (
          <View style={styles.dropdownContainer}>
            <FlatList
              keyboardShouldPersistTaps="handled"
              data={filteredStates}
              keyExtractor={(item) => String(item.stateid)}
              renderItem={({ item }) => (
                <TouchableOpacity testID="stateOption"
                  style={styles.suggestionItem}
                  onPress={() => {
                    try { setReceiverState?.(item.stateAbbrivation); } catch {}
                    setShowSuggestions(false);
                    Keyboard.dismiss();
                  }}
                >
                  <Text style={styles.suggestionText}>{item.stateName} ({item.stateAbbrivation})</Text>
                </TouchableOpacity>
              )}
            />
          </View>
        )}
      </View>
      
      <View style={styles.hintBox}>
        <ShieldIcon width={24} height={24}/>
        <Text style={styles.hintText}>
          {t("YOUR_INFO_IS_ALWAYS_KEPT_PRIVATE")}
        </Text>
      </View>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
    overflow: "visible",
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  input: {
    backgroundColor: "#f5f5f5",
    padding: 12,
    borderRadius: 8,
    fontSize: 16,
    marginBottom: 10,
  },
  suggestionItem: { padding: 12, borderBottomWidth: 1, borderColor: "#eee" },
  suggestionText: { fontSize: 16 },
  form: {
    marginTop: 16,
    overflow: "visible",
    zIndex: 999,
    elevation: 20,
  },
  manualBtn: { marginTop: 16, alignItems: "center" },
  manualText: { fontSize: 16, fontWeight: "600" },
  infoBox: {
    marginTop: 20,
    padding: 12,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 8,
  },
  infoText: { fontSize: 14, color: "#333" },
  hintBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 10,
    padding: 16,
    marginTop: 24,
    backgroundColor: Colors.white,
    marginBottom: 79
  },
  hintText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: '400', 
    color: Colors.darkGrey,
    lineHeight: 18,
    paddingLeft: 16
  },
  dropdownContainer: {
    maxHeight: 180,
    borderWidth: 1,
    borderColor: Colors.textInputBackground,
    borderRadius: 8,
    backgroundColor: Colors.white,
    marginTop: 4,
    position: "relative",
    zIndex: 9999,
    elevation: 30,
  },
});

export default ReceiverAddressStep;